import React, { memo, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { Form, Input, Select } from 'antd';
import { Formik, FormikProps } from 'formik';
import * as yup from 'yup';
import { QuerySearch } from '../form/query-search';
import { BranchApi, PermissionApi } from '../../apis';
import { CYR_NAME, REG_NO } from '../valuation/custom';
import useSWR from 'swr';

const validationSchema = yup.object().shape({
  lastName: yup
    .string()
    .trim()
    .matches(CYR_NAME, 'Зөвхөн крилл үсэг ашиглана уу')
    .required('Овог заавал оруулах'),
  registerNo: yup
    .string()
    .transform((v) => (v ? v.toUpperCase().replace(/\s/g, '') : v))
    .matches(REG_NO, 'Формат: 2 крилл үсэг + 8 тоо (ж: УБ12345678)')
    .required('Регистрийн дугаар заавал оруулах'),

  firstName: yup
    .string()
    .trim()
    .matches(CYR_NAME, 'Зөвхөн крилл үсэг ашиглана уу')
    .required('Нэр заавал оруулах'),
  email: yup
    .string()
    .required('И-мэйл заавал оруулна')
    .matches(/^[\w|_|.|-]{3,120}@([\w\d])+(\.){0,1}[\w\d]{2}\.[\w]{2,3}$/, 'И-мэйл хаяг биш байна'),
  phone: yup.string().required('Утасны дугаар заавал оруулна'),
  branchId: yup.string().required('Салбар заавал оруулна'),
  roleIds: yup.array().min(1, ' Хандах эрхмйг заавал оруулна').required('Рол заавал оруулна'),
});

type FormValues = {
  name: string | undefined;
  nameEng: string | undefined;
};

type Props = {
  onSubmit: (values: any) => void;
  action: any;
};

export type BranchFormRef = {
  submitForm: () => any;
  getValues: () => any;
};

const UserForm = memo(
  forwardRef<BranchFormRef, Props>(({ onSubmit: _onSubmit, action }, ref) => {
    const formikRef = useRef<FormikProps<FormValues>>(null);
    const { data: rols } = useSWR('/api/permission/select', PermissionApi.select);

    console.log('rols :', rols);

    const initialValues = useMemo(() => {
      return {
        registerNo: undefined,
        lastName: undefined,
        firstName: undefined,
        email: undefined,
        phone: undefined,
        avatar: undefined,
        branchId: undefined,
        roleIds: undefined,
        ...(action && {
          registerNo: action.registerNo,
          lastName: action.lastName,
          firstName: action.firstName,
          email: action.email,
          phone: action.phone,
          avatar: action.avatar,
          branchId: action?.branch?.id,
          roleIds: (action.userRoles || []).map((item: any) => item?.id),
        }),
      };
    }, [action]);

    useImperativeHandle(ref, () => ({
      submitForm: async () => {
        const validate = await formikRef.current?.submitForm();
        return validate;
      },
      getValues: () => {},
    }));

    if (!rols) {
      return null;
    }

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={_onSubmit}
        innerRef={formikRef}
      >
        {({ handleChange, values, errors, touched, setFieldValue }) => {
          const filteredOptions = (rols || []).filter(
            (o: string) => !(values.roleIds || []).includes(o)
          );

          return (
            <>
              <Form layout="vertical">
                <div className="card">
                  <div className="inputContainer">
                    <div className="h-[13px]" />

                    <div className="flex flex-row">
                      <div className="flex-1">
                        <Form.Item
                          label="Салбар"
                          layout="vertical"
                          validateStatus={errors.branchId && touched.branchId ? 'error' : ''}
                          help={errors.branchId && touched.branchId ? errors.branchId : ('' as any)}
                        >
                          <QuerySearch
                            value={values.branchId}
                            onChange={(e) => setFieldValue('branchId', e)}
                            placeholder="Салбар"
                            showSearch
                            loadData={BranchApi.select}
                          />
                        </Form.Item>

                        <Form.Item
                          label="Хандах эрх"
                          layout="vertical"
                          validateStatus={errors.roleIds && touched.roleIds ? 'error' : ''}
                          help={errors.roleIds && touched.roleIds ? errors.roleIds : ('' as any)}
                        >
                          <Select
                            mode="multiple"
                            placeholder="Хандах эрх"
                            value={values.roleIds}
                            onChange={(e: any) => setFieldValue('roleIds', e)}
                            style={{ width: '100%' }}
                            options={filteredOptions.map((item: any) => ({
                              value: item.id,
                              label: item.name,
                            }))}
                          />
                        </Form.Item>

                        <Form.Item
                          label="Регистр"
                          layout="vertical"
                          validateStatus={errors.registerNo && touched.registerNo ? 'error' : ''}
                          help={
                            errors.registerNo && touched.registerNo
                              ? errors.registerNo
                              : ('' as any)
                          }
                        >
                          <Input
                            name="registerNo"
                            value={values.registerNo}
                            onChange={handleChange}
                            placeholder="Регистр"
                          />
                        </Form.Item>
                        <Form.Item
                          label="Овог"
                          layout="vertical"
                          validateStatus={errors.lastName && touched.lastName ? 'error' : ''}
                          help={errors.lastName && touched.lastName ? errors.lastName : ('' as any)}
                        >
                          <Input
                            name="lastName"
                            value={values.lastName}
                            onChange={handleChange}
                            placeholder="Овог"
                          />
                        </Form.Item>
                        <Form.Item
                          label="Нэр"
                          layout="vertical"
                          validateStatus={errors.firstName && touched.firstName ? 'error' : ''}
                          help={
                            errors.firstName && touched.firstName ? errors.firstName : ('' as any)
                          }
                        >
                          <Input
                            name="firstName"
                            value={values.firstName}
                            onChange={handleChange}
                            placeholder="Нэр"
                          />
                        </Form.Item>
                        <Form.Item
                          label="И-мэйл"
                          layout="vertical"
                          validateStatus={errors.email && touched.email ? 'error' : ''}
                          help={errors.email && touched.email ? errors.email : ('' as any)}
                        >
                          <Input
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            placeholder="И-мэйл"
                          />
                        </Form.Item>
                        <Form.Item
                          label="Утасны дугаар"
                          layout="vertical"
                          validateStatus={errors.phone && touched.phone ? 'error' : ''}
                          help={errors.phone && touched.phone ? errors.phone : ('' as any)}
                        >
                          <Input
                            name="phone"
                            value={values.phone}
                            onChange={handleChange}
                            placeholder="Утасны дугаар"
                          />
                        </Form.Item>
                      </div>
                      <div className="w-[13px]" />
                    </div>
                  </div>
                </div>
                <div className="h-[20px]" />
              </Form>
            </>
          );
        }}
      </Formik>
    );
  })
);

UserForm.displayName = 'UserForm';

export { UserForm };
